import Page from 'components/layout/Page';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { useI18n } from 'hooks/useI18n';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { useRouter } from 'next/dist/client/router';
import { WP_NextPrev } from 'pages/blog/[slug]';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  WP_REST_API_Categories,
  WP_REST_API_Post,
  WP_REST_API_Tags
} from 'wp-types';
import BlogFloatingCta from '../layout/BlogFloatingCta';
import * as S from './PostComponent.styled';
import PostHeaderCard from './PostHeaderCard';
import { Anchor, PostIndex } from './PostIndex';
import { slugify } from 'utils/slugify';
import { ImageModal } from './imageModal';
import BlogShareModal from './BlogShareModal';
import { faList } from '@fortawesome/pro-light-svg-icons';
import { sortCategories } from '../utils/blogUtils';
import BlogRegisterCta from '../layout/BlogRegisterCta';
import Cta from '../layout/Cta';

type PostComponentProps = {
  disqusShortname?: string;
  post: WP_REST_API_Post & {
    next: WP_NextPrev | null;
    previous: WP_NextPrev | null;
  };
  categories: WP_REST_API_Categories;
  relatedPosts: WP_REST_API_Post[];
  tags: WP_REST_API_Tags;
};

/* TODO: TEMPORAL helper. Investigate to use domutils, html-to-text, etc */
function nodeText(node: any): string {
  if (node.type === 'text') return node.data ?? '';
  if (node.children) return node.children.map(nodeText).join('');
  return '';
}

const PostComponent: React.FC<PostComponentProps> = ({
  post,
  categories,
  relatedPosts,
  tags
}) => {
  const anchorsRef = useRef<Anchor[]>([]);
  const [modalSrc, setModalSrc] = useState<string | null>(null);
  const [shareModalIsOpen, setIsOpen] = useState(false);
  const { t } = useI18n('success-stories');
  const { locale } = useRouter();
  const { user, loadingUser } = useContext(LoggedUserContext);
  const [indexOpen, setIndexOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  function closeImgModal(): void {
    setModalSrc(null);
  }

  function openShareModal(): void {
    setIsOpen(true);
  }

  function closeShareModal(): void {
    setIsOpen(false);
  }

  const postContent = useMemo(() => {
    anchorsRef.current = [];
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode.type === 'tag') {
          const el = domNode as Element;
          if (el.name === 'h2') {
            const text = nodeText(el).trim();
            const id = slugify(text);

            el.attribs = { ...el.attribs, id };
            anchorsRef.current.push({ headerLevel: 2, title: text, id });
          }
          if (el.name === 'img' && el.attribs?.src) {
            const { src, alt } = el.attribs;
            return (
              <img
                {...el.attribs}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setModalSrc(src)}
                alt={alt}
              />
            );
          }
        }
        // @ts-expect-error don't know why, but I cannot use `domNode instanceof Element`, as stated here: https://www.npmjs.com/package/html-react-parser#migration
        if (domNode.attribs?.class === 'signup-banner') {
          return <>{!user && !loadingUser && <BlogRegisterCta />}</>;
        }
      }
    };
    return parse(post.content.rendered, options);
  }, []);

  const sortedCategories = useMemo(
    () => sortCategories(categories),
    [categories]
  );

  useEffect(() => {
    console.log({ relatedPosts, tags });
  });

  return (
    <Page hasScrollTop>
      <S.StyledContainer>
        <S.StyledHeaderWrapper>
          <PostHeaderCard
            post={post}
            sortedCategories={sortedCategories}
            onShareClick={openShareModal}
          />
        </S.StyledHeaderWrapper>
        {/* <Databox
          data={[
            { label: 'label1', value: 'value1' },
            { label: 'label2', value: 'value21' },
            { label: 'label31', value: 'value31' },
            { label: 'label41asdasd asdfwadfgkhgqg', value: 'value41' }
          ]}
        /> */}
        <S.StyledMainLayout>
          <S.StyledPostIndexWrapper>
            <S.StyledPostIndexSticky>
              <PostIndex post={post} />
            </S.StyledPostIndexSticky>
          </S.StyledPostIndexWrapper>
          <S.StyledPostContentWrapper>
            <S.StyledPostContent>{postContent}</S.StyledPostContent>
          </S.StyledPostContentWrapper>
        </S.StyledMainLayout>

        <S.StyledRecommendedPosts
          relatedPosts={relatedPosts}
          currentPost={post}
          categories={categories}
          locale={locale}
          tags={tags}
        />

        <Cta alt />
        <S.StyledFloatingBannerContainer>
          <BlogFloatingCta />
        </S.StyledFloatingBannerContainer>
      </S.StyledContainer>

      <ImageModal
        isOpen={Boolean(modalSrc)}
        src={modalSrc}
        onClose={closeImgModal}
      />
      <BlogShareModal
        slug={post.slug}
        shareModalIsOpen={shareModalIsOpen}
        shareModalClose={closeShareModal}
      />
      <S.IndexToggleButton
        $isOpen={drawerOpen}
        label=""
        onClick={() => (drawerOpen ? closeDrawer() : openDrawer())}
        schema={'neutral'}
        startIcon={faList}
      />

      {drawerOpen ? (
        <S.DrawerOverlay onClick={closeDrawer}>
          <S.IndexDrawer open={drawerOpen} onClick={(e) => e.stopPropagation()}>
            <S.DrawerCloseButton onClick={closeDrawer}>×</S.DrawerCloseButton>
            <S.IndexDrawerContent onClick={closeDrawer}>
              <PostIndex post={post} />
            </S.IndexDrawerContent>
          </S.IndexDrawer>
        </S.DrawerOverlay>
      ) : null}
    </Page>
  );
};

export default PostComponent;
